import { useLocation, useNavigate, useParams } from "react-router";
import { IDataType } from "../definitions/datatypes";

export interface IClassesObject {
  [index:string]: boolean | undefined;
}

export interface IObject {
  [index:string]: string;
}

export interface IChartDataset {
  label: string,
  data: string[] | number[] | string,
  borderColor: string,
  backgroundColor: string,
  key: string,
  yAxisID?: string
}

export interface IMultyChart {
  datasets: IChartDataset[],
  labels : string[]
}

export const appendixOfy1 = ["frac", "score", "fpr", "recall", "precision", "accuracy", "tpr"];

export function classes(classObject: IClassesObject, classes?: string) {
  const newClasses = Object.keys(classObject)
    .filter(key => classObject[key])
    .reduce((acc, currentValue) =>
      acc === "" ? currentValue : `${acc} ${currentValue}`,
    "");
  return classes ? `${classes} ${newClasses}` : newClasses;
}

export function createUUID() {
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, function(c) {
    const r = Math.random() * 16 | 0, v = c == "x" ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });
}

export interface WithRouterProps {
  location: ReturnType<typeof useLocation>;
  params: Record<string, string>;
  navigate: ReturnType<typeof useNavigate>;
}

export const withRouter = <Props extends WithRouterProps>(
  Component: React.ComponentType<Props>  
) => {
  return (props: Omit<Props, keyof WithRouterProps>) => {
    const location = useLocation();
    const params = useParams();
    const navigate = useNavigate();
    
    return (
      <Component
        {...(props as Props)}
        location={location}
        params={params}
        navigate={navigate}
      />
    );
  };
};

export interface IStatusValidation {
  [key:string]: string | null  
}

export const validateLoadingStatusNew = (data: (string | null | undefined)[]) => {
  let pendingStatus = false;
  data.forEach((status) => {
    if (status && status === "pending") {
      pendingStatus = true;
    }
  });
  return pendingStatus;
};

export const getTheLastParam = () => {
  const str = location.pathname;
  const n = str.lastIndexOf("/");
  const result = str.substring(n + 1);
  return result;
};

export const getTheParamBeforeID = (paramId: string | undefined) => {
  const makePathIntoArray = location.pathname.split("/");
  const findIdIndex = paramId && location.pathname.split("/").indexOf(paramId);
  return findIdIndex && makePathIntoArray[findIdIndex - 1];
};

export const capitaliseString = (string: string) => string.charAt(0).toUpperCase() + string.slice(1);

export const rationToPercents2Decimals = (num: number, dec = 2) => {
  let shortenVals = (num * 100).toFixed(dec);
  if (shortenVals.slice(-2) === "00") {
    shortenVals = (num * 100).toFixed(0);
  }
  else if (shortenVals.slice(-1) === "0") {
    shortenVals = (num * 100).toFixed(1);
  }
  return `${shortenVals}%`;
};

export const convertKeyIntoName = (str: string) => {
  const newStr = str.replace(/_/g, " ");
  const stringIntoArr = newStr.split(" ");
  let result = "";
  
  stringIntoArr.forEach((subKeyString) => {
    if (subKeyString === "frac") {
      return "%";
    }
    else if (subKeyString.length <= 2) {
      result =  result + " " + subKeyString.toUpperCase();
    }
    else if (subKeyString) {
      result = result + " " + subKeyString.charAt(0).toUpperCase() + subKeyString.slice(1);
    }
  });
  return result;
};

export const moveArrayElement = (array:any, newIndex:number, oldIndex:number) => { // eslint-disable-line
  if (newIndex >= array.length) {
    let k = newIndex - array.length + 1;
    while (k--) {
      array.push(undefined);
    }
  }
  array.splice(newIndex, 0, array.splice(oldIndex, 1)[0]);
  return array;
};

export const filterArrOfObjByString = (data: any, objKey: string, filterByVal : string) => { // eslint-disable-line
  return data.filter((opt:IObject) => opt[objKey].toLowerCase().search(filterByVal.toLowerCase()) > -1);
};

export const sortByAlphabet = (data:any, sortByKeyStringVal: string) => { // eslint-disable-line
  return data.sort(function (a:IObject, b:IObject) {
    if (a[sortByKeyStringVal] < b[sortByKeyStringVal]) {
      return -1;
    }
    if (a[sortByKeyStringVal] > b[sortByKeyStringVal]) {
      return 1;
    }
    return 0;
  });
};

export const randomHexColorCode = () => {
  const num = (Math.random() * 0xfffff * 1000000).toString(16);
  return "#" + num.slice(0, 6);
};

export const transformName = (state: string | null) => {
  switch (state) {
    case "test":
      return "Testing";
    case "pending_production_approval":
      return "Pending Production Approval";
    case "production":
      return "Production";
    default:
      return "None";
  }
};

export const onlyUnique = (value:string, index:number, array:any) => { // eslint-disable-line
  return array.indexOf(value) === index;
};

export const transformObjectsFracs = (row: IObject) => {
  let preparePercentskeys = {};

  for (const [key, value] of Object.entries(row)) {
    let updatedValue = value;

    if (key.includes("frac") && typeof value === "number") {
      updatedValue = rationToPercents2Decimals(value);
    }
    preparePercentskeys = {
      ...preparePercentskeys,
      [key]: updatedValue
    };
  }
  return preparePercentskeys;
};

export const defineStep = (thresholds?: number) => {
  switch (thresholds) {
    case 11:
      return 0.1;
    case 101:
      return 0.01;
    case 1001:
      return 0.001;
    default:
      return 0.1;
  }
};

export const defineStatus = (data: any) => { // eslint-disable-line
  return data.map((row:any) => { // eslint-disable-line
    let finalStatus = "";
    if (new Date(new Date().getTime()) > new Date(new Date(row.expiration_date).getTime())) {
      // today > x.expiration_date => expired
      finalStatus = "Expired";
    }
    if (new Date(new Date().getTime() + 2628000000)  > new Date(new Date(row.expiration_date).getTime())) {
      // (today + 1 month) > x.expiration_date => expires soon
      finalStatus = "Expires soon";
    }
    if (new Date(new Date().getTime() + 2628000000)  < new Date(new Date(row.expiration_date).getTime())) {
      // (today + 1 month) < x.expiration_date => active
      finalStatus = "Active";
    }
    return {
      ...row,
      status: finalStatus
    };
  });
};

export const backendDataTypeToFrontend = (dataType: string): IDataType => {
  switch (dataType) {
    case "datetime":
      return { type: "dateTime" };
    case "text":
      return { type: "text" };
    case "reference":
      return { type: "reference", values: [] };
    case "enumeration":
      return { type: "enumeration", values: [] };
    case "textarea":
      return { type: "textarea" };
    case "noDisplayCol":
      return { type: "noDisplayCol" };
    case "number":
      return { type: "number" };
    case "float":
      return { type: "number", decimalPlaces: 2 };
    default:
      return { type: "text" };
  }
};